/**
 * Profile API Service for Wadakaroo
 * Handles all profile-related API calls.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Profile CRUD Endpoints
// ============================================

/**
 * Get paginated list of profiles
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-based)
 * @param {number} params.size - Page size
 * @param {boolean} params.activeOnly - Filter active profiles only
 * @param {string} params.search - Search term for profile name
 */
export const getProfilesApi = async ({ page = 0, size = 10, activeOnly = false, search = '' } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    params.append('activeOnly', activeOnly.toString());
    if (search) {
        params.append('search', search);
    }
    
    const response = await axiosInstance.get(`/profiles?${params.toString()}`);
    return response.data;
};

/**
 * Get profile summaries for dropdowns
 * @param {boolean} activeOnly - Filter active profiles only
 */
export const getProfileSummariesApi = async (activeOnly = true) => {
    const response = await axiosInstance.get(`/profiles/summary?activeOnly=${activeOnly}`);
    return response.data;
};

/**
 * Get a single profile by UUID
 * @param {string} uuid - Profile UUID
 */
export const getProfileApi = async (uuid) => {
    const response = await axiosInstance.get(`/profiles/${uuid}`);
    return response.data;
};

/**
 * Create a new profile
 * @param {Object} profileData - Profile creation data
 * @param {string} profileData.name - Profile name
 * @param {string} profileData.description - Profile description
 * @param {string[]} profileData.permissions - Array of permission codes
 */
export const createProfileApi = async (profileData) => {
    const response = await axiosInstance.post('/profiles', profileData);
    return response.data;
};

/**
 * Update an existing profile
 * @param {string} uuid - Profile UUID
 * @param {Object} profileData - Profile update data
 */
export const updateProfileApi = async (uuid, profileData) => {
    const response = await axiosInstance.put(`/profiles/${uuid}`, profileData);
    return response.data;
};

/**
 * Delete a profile
 * @param {string} uuid - Profile UUID
 */
export const deleteProfileApi = async (uuid) => {
    const response = await axiosInstance.delete(`/profiles/${uuid}`);
    return response.data;
};

// ============================================
// Permission Endpoints
// ============================================

/**
 * Get all available permissions
 */
export const getPermissionsApi = async () => {
    const response = await axiosInstance.get('/profiles/permissions');
    return response.data;
};

/**
 * Get permissions grouped by category
 */
export const getPermissionsGroupedApi = async () => {
    const response = await axiosInstance.get('/profiles/permissions/grouped');
    return response.data;
};

// ============================================
// Default Export
// ============================================

const profileApi = {
    getProfilesApi,
    getProfileSummariesApi,
    getProfileApi,
    createProfileApi,
    updateProfileApi,
    deleteProfileApi,
    getPermissionsApi,
    getPermissionsGroupedApi,
};

export default profileApi;
